'use client';
import React, { useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, CheckCircle2, HelpCircle, AlertTriangle } from 'lucide-react';

interface DistractorItem {
  id: string;
  answer: string;
  trapType: string;
  explanation: string;
  howToAvoid: string;
}

interface WalkthroughQuestion {
  id: string;
  typeCode: string;
  typeLabel: string;
  question: string;
  correctAnswer: string;
  correctReasoning: string;
  paraphraseMap: Array<{ id: string; passageText: string; questionText: string }>;
  distractors: DistractorItem[];
  strategyTip: { title: string; steps: Array<{ id: string; text: string }> };
}

interface Props {
  question: WalkthroughQuestion;
  understood: boolean;
  confused: boolean;
  onUnderstood: () => void;
  onConfused: () => void;
  onPrev: () => void;
  onNext: () => void;
  isFirst: boolean;
  isLast: boolean;
}

export default function WalkthroughQuestionPanel({
  question, understood, confused, onUnderstood, onConfused, onPrev, onNext, isFirst, isLast,
}: Props) {
  const [openDistractor, setOpenDistractor] = useState<string | null>(null);
  const [paraphraseOpen, setParaphraseOpen] = useState(true);
  const [distractorOpen, setDistractorOpen] = useState(true);

  return (
    <div className="flex flex-col min-h-0 h-full overflow-hidden border-l border-border">
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto scrollbar-thin px-3 sm:px-5 py-3 sm:py-5 space-y-3 sm:space-y-5">

        {/* Question */}
        <div className="p-3 sm:p-4 rounded-xl bg-muted border border-border">
          <div className="flex items-center gap-2 mb-2">
            <span className="badge-qtype bg-info-bg text-info border border-info/30 text-xs">{question.typeLabel}</span>
          </div>
          <p className="text-sm font-medium text-foreground leading-relaxed">{question.question}</p>
        </div>

        {/* Correct answer */}
        <div className="p-3 sm:p-4 rounded-xl bg-success-bg border border-accent/30">
          <div className="flex items-center gap-2 mb-1.5 sm:mb-2">
            <CheckCircle2 size={15} className="text-accent flex-shrink-0" />
            <p className="text-sm font-semibold text-accent">Correct Answer: {question.correctAnswer}</p>
          </div>
          <p className="text-sm text-foreground leading-relaxed">{question.correctReasoning}</p>
        </div>

        {/* Paraphrase map — collapsible on mobile */}
        <div className="border border-border rounded-xl overflow-hidden">
          <button
            onClick={() => setParaphraseOpen((o) => !o)}
            className="w-full flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 bg-muted/40 hover:bg-muted/70 transition-colors text-left"
          >
            <div>
              <p className="text-xs font-semibold text-foreground uppercase tracking-wide">Paraphrase Map</p>
              <p className="text-xs text-muted-foreground mt-0.5 hidden sm:block">How the examiner rewrote passage language in the question</p>
            </div>
            <ChevronDown
              size={15}
              className={`text-muted-foreground flex-shrink-0 transition-transform duration-200 ${paraphraseOpen ? 'rotate-180' : ''}`}
            />
          </button>
          {paraphraseOpen && (
            <div className="px-3 sm:px-4 py-3 space-y-2 bg-card">
              <p className="text-xs text-muted-foreground sm:hidden mb-2">How the examiner rewrote passage language:</p>
              {question.paraphraseMap.map((pm) => (
                <div key={pm.id} className="flex items-start sm:items-center gap-1.5 sm:gap-2 flex-wrap">
                  <span className="paraphrase-pill-passage text-xs">{pm.passageText}</span>
                  <span className="text-muted-foreground text-xs">→</span>
                  <span className="paraphrase-pill-question text-xs">{pm.questionText}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Distractor breakdown — collapsible on mobile */}
        <div className="border border-border rounded-xl overflow-hidden">
          <button
            onClick={() => setDistractorOpen((o) => !o)}
            className="w-full flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 bg-muted/40 hover:bg-muted/70 transition-colors text-left"
          >
            <div>
              <p className="text-xs font-semibold text-foreground uppercase tracking-wide">Distractor Breakdown</p>
              <p className="text-xs text-muted-foreground mt-0.5 hidden sm:block">Why each wrong option was designed to trap a specific misreading</p>
            </div>
            <ChevronDown
              size={15}
              className={`text-muted-foreground flex-shrink-0 transition-transform duration-200 ${distractorOpen ? 'rotate-180' : ''}`}
            />
          </button>
          {distractorOpen && (
            <div className="px-3 sm:px-4 py-3 space-y-2 bg-card">
              <p className="text-xs text-muted-foreground sm:hidden mb-2">Why each wrong option traps a specific misreading:</p>
              {question.distractors.map((dist) => (
                <div key={dist.id} className="border border-border rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenDistractor(openDistractor === dist.id ? null : dist.id)}
                    className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-2.5">
                      <span className="inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-danger-bg border border-danger/30 text-xs font-bold text-danger flex-shrink-0">
                        {dist.answer.slice(0, 2)}
                      </span>
                      <div className="min-w-0">
                        <p className="text-xs sm:text-sm font-semibold text-foreground">Answer: {dist.answer}</p>
                        <span className="distractor-trap-label text-xs">{dist.trapType}</span>
                      </div>
                    </div>
                    <ChevronDown
                      size={14}
                      className={`text-muted-foreground flex-shrink-0 ml-2 transition-transform duration-200 ${openDistractor === dist.id ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {openDistractor === dist.id && (
                    <div className="px-3 sm:px-4 pb-3 pt-1 space-y-2.5 bg-muted/20 animate-slide-up">
                      <div>
                        <p className="text-xs font-semibold text-foreground mb-1">Why candidates pick this:</p>
                        <p className="text-xs text-foreground leading-relaxed">{dist.explanation}</p>
                      </div>
                      <div className="flex items-start gap-2 p-2.5 rounded-lg bg-info-bg border border-info/20">
                        <AlertTriangle size={12} className="text-info flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-semibold text-info mb-0.5">How to avoid this trap:</p>
                          <p className="text-xs text-foreground leading-relaxed">{dist.howToAvoid}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Sticky CTA footer ── */}
      <div className="flex-shrink-0 px-3 sm:px-4 py-3 border-t border-border bg-card space-y-2.5">
        {!understood && !confused && (
          <div className="flex gap-2">
            <button
              onClick={onConfused}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 sm:py-2.5 rounded-lg border-2 border-warning/40 bg-warning-bg text-warning text-xs sm:text-sm font-semibold hover:bg-warning-bg/80 transition-colors"
            >
              <HelpCircle size={13} />
              <span className="hidden xs:inline">Still unsure</span>
              <span className="xs:hidden">Flag</span>
            </button>
            <button
              onClick={onUnderstood}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 sm:py-2.5 rounded-lg border-2 border-accent/40 bg-success-bg text-accent text-xs sm:text-sm font-semibold hover:bg-success-bg/80 transition-colors"
            >
              <CheckCircle2 size={13} />
              <span className="hidden xs:inline">Understood</span>
              <span className="xs:hidden">Got it</span>
            </button>
          </div>
        )}
        {understood && (
          <div className="flex items-center gap-2 p-2.5 rounded-lg bg-success-bg border border-accent/30">
            <CheckCircle2 size={13} className="text-accent flex-shrink-0" />
            <p className="text-xs text-accent font-semibold leading-snug">Marked as understood — this concept will appear in your spaced-repetition review in 4 days</p>
          </div>
        )}
        {confused && (
          <div className="flex items-center gap-2 p-2.5 rounded-lg bg-warning-bg border border-warning/30">
            <HelpCircle size={13} className="text-warning flex-shrink-0" />
            <p className="text-xs text-warning font-semibold leading-snug">Flagged for review — this question type will be prioritised in your next practice set</p>
          </div>
        )}
        {/* Prev / Next — only shown on lg+ (mobile uses top-bar arrows) */}
        <div className="hidden lg:flex items-center justify-between">
          <button onClick={onPrev} disabled={isFirst} className="btn-outline py-2 px-4 text-xs flex items-center gap-1.5 disabled:opacity-40">
            <ChevronLeft size={13} /> Previous
          </button>
          <button onClick={onNext} disabled={isLast} className="btn-primary py-2 px-4 text-xs flex items-center gap-1.5 disabled:opacity-40">
            Next <ChevronRight size={13} />
          </button>
        </div>
        {/* Mobile prev/next full-width buttons */}
        <div className="flex lg:hidden items-center gap-2">
          <button onClick={onPrev} disabled={isFirst} className="flex-1 btn-outline py-2 text-xs flex items-center justify-center gap-1.5 disabled:opacity-40">
            <ChevronLeft size={13} /> Prev
          </button>
          <button onClick={onNext} disabled={isLast} className="flex-1 btn-primary py-2 text-xs flex items-center justify-center gap-1.5 disabled:opacity-40">
            Next <ChevronRight size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}